import { prettyDate } from "@app/date";
import type { OperationStatus } from "@app/types";
import cn from "classnames";
import { IconCheck, IconInfo, IconSettings, IconX } from "./icons";

// TODO: Add support for StatusVariant | "pending"
export type PillVariant =
  | "default"
  | "pending"
  | "progress"
  | "success"
  | "error";

const pillStyles = {
  pending: "text-brown border-brown bg-orange-100",
  error: "text-red border-red-300 bg-red-100",
  progress: "text-indigo border-indigo-300 bg-indigo-100",
  success: "text-forest border-lime-300 bg-lime-100",
};

// Kept separate from `pillStyles` (which `StatusPill` still uses).
const PILL_VARIANT_TOKENS: Record<
  PillVariant,
  {
    text?: string;
    border: string;
    bg: string;
    hoverBg: string;
    hoverBorder: string;
  }
> = {
  default: {
    border: "border-black-300",
    bg: "bg-black-50",
    hoverBg: "hover:bg-black-100",
    hoverBorder: "hover:border-black-600",
  },
  pending: {
    text: "text-orange-700",
    border: "border-orange-500",
    bg: "bg-orange-100",
    hoverBg: "hover:bg-orange-200",
    hoverBorder: "hover:border-orange-600",
  },
  progress: {
    text: "text-indigo-600",
    border: "border-indigo",
    bg: "bg-indigo-100",
    hoverBg: "hover:bg-indigo-150",
    hoverBorder: "hover:border-indigo-600",
  },
  error: {
    text: "text-red-400",
    border: "border-red-300",
    bg: "bg-red-100",
    hoverBg: "hover:bg-red-150",
    hoverBorder: "hover:border-red-400",
  },
  success: {
    text: "text-lime-700",
    border: "border-lime-300",
    bg: "bg-lime-100",
    hoverBg: "hover:bg-lime-200",
    hoverBorder: "hover:border-lime-500",
  },
};

const pillVariantStyles: Record<PillVariant, string> = Object.fromEntries(
  Object.entries(PILL_VARIANT_TOKENS).map(([variant, t]) => [
    variant,
    cn(t.text, t.border, t.bg, t.hoverBg, t.hoverBorder),
  ]),
) as Record<PillVariant, string>;

export const Pill = ({
  children,
  className = "",
  variant = "default",
  icon = null,
  title = "",
}: {
  children: React.ReactNode;
  className?: string;
  variant?: PillVariant;
  icon?: React.ReactNode;
  title?: string;
}) => {
  const defaultClassName = cn(
    "rounded-full border",
    // Explicit px, not rem: app root font-size is 14px.
    "text-[12px] leading-none font-medium tracking-[0.04em] text-black-500",
    "h-[16px] px-[8px] py-[2px] flex gap-[2px] justify-center items-center w-fit",
    "[&_svg]:w-[12px] [&_svg]:h-[12px] [&_svg]:stroke-2",
    pillVariantStyles[variant],
  );
  return (
    <div className={`${defaultClassName} ${className}`}>
      {icon}
      <div title={title}>{children}</div>
    </div>
  );
};

export const StatusPill = ({
  status,
  from,
}: {
  status: OperationStatus;
  from: string;
}) => {
  const date = prettyDate(from);

  const className = cn(
    "rounded-full border-2",
    "text-sm font-semibold ",
    "px-2 flex justify-between items-center w-fit",
  );

  if (status === "running" || status === "queued") {
    return (
      <div className={cn(className, pillStyles.pending)} role="status">
        <IconSettings color="currentColor" className="mr-1" variant="sm" />
        <div>
          {status === "running" ? "Building" : "Queued"} {date}
        </div>
      </div>
    );
  }

  if (status === "failed" || status === "canceled") {
    return (
      <div className={cn(className, pillStyles.error)} role="status">
        <IconX color="currentColor" variant="sm" />
        <div>
          {status === "canceled" ? "Canceled" : "Failed"} {date}
        </div>
      </div>
    );
  }

  if (status === "succeeded") {
    return (
      <div className={cn(className, pillStyles.success)} role="status">
        <IconCheck color="currentColor" className="mr-1" variant="sm" />
        Deployed {date}
      </div>
    );
  }

  return (
    <div className={cn(className, pillStyles.progress)} role="status">
      <IconInfo color="currentColor" className="mr-1" variant="sm" />
      Not deployed
    </div>
  );
};
